import { useEffect, useState } from 'react'

/** `true` when the media query matches. Desktop (true) when matchMedia is unavailable. */
export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : true)
  const [matches, setMatches] = useState(get)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}

/** md breakpoint (768px) and up. Matches Tailwind's `md:`. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)')
}

/** The value, `delay` ms after it last changed. */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

/** Sets document.title as "<title> · ScoreLM Ops". */
export function useDocumentTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · ScoreLM Ops` : 'ScoreLM Ops'
  }, [title])
}
